import React, { useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Form, Col } from 'react-bootstrap';
import Button from '../../../../components/Button';
import If from '../../../../components/If';
import AlertError from '../../../../components/AlertError';
import AlertSuccess from '../../../../components/AlertSuccess';
import Loading from '../../../../components/Loading';
import { STRINGS } from '../../../../util/strings';
import {
	CulturaRegiaoOpcao,
	RecResponsavelTecnicoExtUsoForm,
	RecResponsavelTecnicoExtUsoResults
} from '../../../../models/models';
import api from '../../../../services/api';
import '../../styles_edit.css';

interface stateType {
	id: number;
}

interface TecnicoOpcao {
	userName: string;
	nome: string;
	email: string;
}

function renderApiError(error: any) {
	if (!error) {
		return null;
	}
	const msg = error.message;
	if (Array.isArray(msg) && msg.length > 0) {
		return msg.map((m: string) => <AlertError key={m} message={m} />);
	}
	if (typeof msg === 'string' && msg.length > 0) {
		return <AlertError message={msg} />;
	}
	return <AlertError message={STRINGS.ALERT_GENERIC_ERROR} />;
}

function normalizeArray<T>(data: unknown): T[] {
	return Array.isArray(data) ? data : [];
}

export default function RecResponsavelTecnicoExtUsoEdit() {
	const history = useHistory();
	const location = useLocation<stateType>();
	const [successfulResponse, setSuccessfulResponse] = useState<boolean>(false);
	const [loading, setLoading] = useState<boolean>(true);
	const [loadingSubmit, setLoadingSubmit] = useState<boolean>(false);
	const [errorRequest, setErrorRequest] = useState<any>(null);
	const [regioes, setRegioes] = useState<CulturaRegiaoOpcao[]>([]);
	const [tecnicos, setTecnicos] = useState<TecnicoOpcao[]>([]);

	const schema = yup.object().shape({
		culturaRegiaoId: yup.number().min(1, STRINGS.FORM_FIELD_REQUIRED).required(STRINGS.FORM_FIELD_REQUIRED),
		userName: yup.string().required(STRINGS.FORM_FIELD_REQUIRED),
		ativo: yup.boolean()
	});

	const { handleSubmit, register, errors, reset, control, watch, setValue } = useForm({
		mode: 'onTouched',
		resolver: yupResolver(schema)
	});

	const userNameWatch = watch('userName');

	useEffect(() => {
		const loadOptions = async () => {
			const [regRes, tecRes] = await Promise.all([
				api.get<CulturaRegiaoOpcao[]>('api/v1/cultivares/RecResponsavelTecnicoExtensaoUso/cultura-regioes'),
				api.get<TecnicoOpcao[]>('api/v1/cultivares/RecResponsavelTecnicoExtensaoUso/tecnicos-extensao-uso')
			]);
			setRegioes(normalizeArray<CulturaRegiaoOpcao>(regRes.data));
			setTecnicos(normalizeArray<TecnicoOpcao>(tecRes.data));
		};

		const loadItem = async () => {
			try {
				await loadOptions();
				const { data } = await api.get<RecResponsavelTecnicoExtUsoResults>(
					`api/v1/cultivares/RecResponsavelTecnicoExtensaoUso/${location.state.id}`
				);
				reset({
					culturaRegiaoId: data.culturaRegiaoId,
					userName: data.userName,
					nomeResponsavel: data.nomeResponsavel,
					emailResponsavel: data.emailResponsavel,
					ativo: data.ativo
				});
			} catch (err: any) {
				setErrorRequest(err?.response?.data ?? { message: STRINGS.ALERT_GENERIC_ERROR });
			}
			setLoading(false);
		};
		loadItem();
	}, [location.state.id, reset]);

	useEffect(() => {
		const t = tecnicos.find((x) => x.userName === userNameWatch);
		if (t) {
			setValue('nomeResponsavel', t.nome ?? '');
			setValue('emailResponsavel', t.email ?? '');
		}
	}, [userNameWatch, tecnicos, setValue]);

	const onSubmit = async (formData: RecResponsavelTecnicoExtUsoForm) => {
		setLoadingSubmit(true);
		setErrorRequest(null);
		try {
			const { status } = await api.put('api/v1/cultivares/RecResponsavelTecnicoExtensaoUso', {
				id: location.state.id,
				culturaRegiaoId: Number(formData.culturaRegiaoId),
				userName: formData.userName,
				nomeResponsavel: formData.nomeResponsavel,
				emailResponsavel: formData.emailResponsavel,
				ativo: formData.ativo
			});
			if (status === 200) {
				setSuccessfulResponse(true);
				setTimeout(() => history.goBack(), 2000);
			}
		} catch (err: any) {
			setErrorRequest(err?.response?.data ?? { message: STRINGS.ALERT_GENERIC_ERROR });
		}
		setLoadingSubmit(false);
	};

	if (loading) return <Loading />;

	return (
		<div className="container container-fluid">
			<If condition={!!errors.culturaRegiaoId || !!errors.userName}>
				<AlertError message={STRINGS.ALERT_WARNING_FIELDS} />
			</If>
			<If condition={successfulResponse}>
				<AlertSuccess message={STRINGS.ALERT_MESSAGE_SUCCESS_EDIT} />
			</If>
			{!successfulResponse && errorRequest && renderApiError(errorRequest)}
			<h3>Editar responsável REC (Extensão de Uso)</h3>
			<Form onSubmit={handleSubmit(onSubmit)}>
				<Form.Row>
					<Form.Group as={Col} md={4}>
						<Form.Label>REC *</Form.Label>
						<Controller
							name="culturaRegiaoId"
							control={control}
							render={({ onChange, value }) => (
								<Form.Control
									as="select"
									value={value || ''}
									onChange={(e) => onChange(Number(e.target.value))}
								>
									<option value="">Selecione...</option>
									{regioes.map((r) => (
										<option key={r.id} value={r.id}>
											{r.descricao}
										</option>
									))}
								</Form.Control>
							)}
						/>
					</Form.Group>
					<Form.Group as={Col} md={4}>
						<Form.Label>Técnico responsável *</Form.Label>
						<Controller
							name="userName"
							control={control}
							render={({ onChange, value }) => (
								<Form.Control
									as="select"
									value={value || ''}
									onChange={(e) => onChange(e.target.value)}
								>
									<option value="">Selecione...</option>
									{tecnicos.map((t) => (
										<option key={t.userName} value={t.userName}>
											{t.nome} — {t.email}
										</option>
									))}
								</Form.Control>
							)}
						/>
					</Form.Group>
					<Form.Group as={Col} md={4}>
						<Form.Label>Ativo</Form.Label>
						<Form.Check type="checkbox" ref={register} name="ativo" label="Ativo" />
					</Form.Group>
				</Form.Row>
				<Form.Row>
					<Form.Group as={Col} md={6}>
						<Form.Label>Nome</Form.Label>
						<Form.Control readOnly ref={register} name="nomeResponsavel" />
					</Form.Group>
					<Form.Group as={Col} md={6}>
						<Form.Label>E-mail</Form.Label>
						<Form.Control readOnly ref={register} name="emailResponsavel" />
					</Form.Group>
				</Form.Row>
				<div className="button-position">
					<Button title="Salvar" type="submit" contained isLoading={loadingSubmit} />
					<Button title="Voltar" type="button" contained={false} onClick={() => history.goBack()} />
				</div>
			</Form>
		</div>
	);
}
